import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { EmojiButton } from '../../components/ui/EmojiPicker';
import { Field } from '../../components/ui/Field';
import { Select, Textarea } from '../../components/ui/form';
import { ApiError } from '../../lib/api';
import { useEmojiComposer } from '../../lib/emoji';
import { Modal } from './Modal';
import { albums, type Album } from './api';

/**
 * Create or edit an album.
 *
 * One component for both because the fields are the same and the difference is
 * a single call. Two would drift - the create form gaining a field the edit
 * form never learned about is exactly how that happens.
 */
export function AlbumEditor({
  album,
  onClose,
  onSaved,
}: {
  /** Absent when creating. */
  album?: Album;
  onClose: () => void;
  onSaved: (album: Album) => void;
}) {
  const [title, setTitle] = useState(album?.title ?? '');
  const [description, setDescription] = useState(album?.description ?? '');
  const [visibility, setVisibility] = useState<Album['visibility']>(album?.visibility ?? 'public');

  const emoji = useEmojiComposer(description, setDescription);

  const save = useMutation({
    mutationFn: () => {
      const draft = {
        title: title.trim(),
        description: description.trim() || null,
        visibility,
      };

      return album ? albums.update(album.id, draft) : albums.create(draft);
    },
    onSuccess: onSaved,
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (!title.trim()) return;

    save.mutate();
  };

  return (
    <Modal title={album ? 'Edit album' : 'New album'} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-5" data-album-form>
        <Field
          label="Title"
          name="title"
          value={title}
          maxLength={120}
          required
          autoFocus
          disabled={save.isPending}
          onChange={(event) => setTitle(event.target.value)}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="album-description" className="text-sm font-medium text-soft">
            Description
          </label>
          <Textarea
            id="album-description"
            ref={emoji.ref as React.RefObject<HTMLTextAreaElement>}
            className="min-h-24"
            value={description}
            maxLength={2000}
            disabled={save.isPending}
            placeholder="What is this release? Optional."
            onChange={(event) => setDescription(event.target.value)}
          />
          <div className="-mt-0.5 flex">
            <EmojiButton onSelect={emoji.insert} direction="down" align="left" />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="album-visibility" className="text-sm font-medium text-soft">
            Who can see it
          </label>
          <Select
            id="album-visibility"
            value={visibility}
            disabled={save.isPending}
            data-album-visibility
            options={[
                { value: 'public', label: 'Everyone' },
                { value: 'unlisted', label: 'Anyone with the link' },
                { value: 'private', label: 'Only me' },
              ]}
            onChange={(next) => setVisibility(next as Album['visibility'])}
          />
          <p className="text-xs text-faint">
            {/*
              Spelled out because the interaction surprises people: an album's
              visibility and its tracks' visibility are separate, which is what
              lets somebody stage a release publicly while its tracks are still
              private.
            */}
            Tracks keep their own visibility. A private track stays hidden even on a public album.
          </p>
        </div>

        {save.isError && (
          <Alert tone="error">
            {save.error instanceof ApiError ? save.error.message : 'That could not be saved.'}
          </Alert>
        )}

        <div className="flex items-center gap-3">
          <Button type="submit" loading={save.isPending} disabled={!title.trim()}>
            {album ? 'Save changes' : 'Create album'}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
